import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Award, Clock, Users, ChevronRight, Check, Phone, Mail, Building2, Globe, User, MapPin, TrendingUp, Target, ChevronLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_award-winning-leads/artifacts/qd61bkjm_iogroyll_Brand%20Partners_logo.png";

// Separate before/after images for each client (updated)
const MIAMI_BEFORE = "https://customer-assets.emergentagent.com/job_award-winning-leads/artifacts/9xx27ubj_Miami-before.png";
const MIAMI_AFTER = "https://customer-assets.emergentagent.com/job_award-winning-leads/artifacts/t4jv3j0t_Miami-after.png";
const BUCKEYE_BEFORE = "https://customer-assets.emergentagent.com/job_award-winning-leads/artifacts/qp1z87a2_Buckeye-before.png";
const BUCKEYE_AFTER = "https://customer-assets.emergentagent.com/job_award-winning-leads/artifacts/wjrgdfm7_Buckeye-after.png";

// Hero ranking image from reference
const HERO_RANKING_IMAGE = "https://storage.googleapis.com/msgsndr/HsbNUtiNuf2Zs7vGUidj/media/6984b3c80708e41759d780bd.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Before/After Comparison Slider Component with separate images
const ComparisonSlider = ({ beforeImage, afterImage, title }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div className="space-y-4">
      {title && <h3 className="text-xl font-bold text-center text-gray-800">{title}</h3>}
      <div 
        ref={containerRef}
        className="comparison-slider relative w-full cursor-ew-resize select-none rounded-2xl overflow-hidden shadow-2xl"
        style={{ aspectRatio: '1/1' }}
        onMouseDown={handleMouseDown}
        onTouchMove={handleTouchMove}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
        data-testid="comparison-slider"
      >
        {/* After image (full background) */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${afterImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        {/* Before image (clipped on left) */}
        <div 
          className="absolute top-0 left-0 h-full overflow-hidden"
          style={{ width: `${sliderPosition}%` }}
        >
          <div 
            className="h-full"
            style={{
              width: containerWidth || '100%',
              backgroundImage: `url(${beforeImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
        </div>

        {/* Labels */}
        <span className="slider-label before">BEFORE</span>
        <span className="slider-label after">AFTER</span>

        {/* Slider handle */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center">
            <ChevronLeft className="w-4 h-4 text-gray-600" />
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Lead Form Component
const LeadForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    website_url: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.company_name) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company_name: "",
        website_url: ""
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="gradient-border p-8 md:p-10 rounded-2xl bg-white shadow-2xl shadow-blue-500/10" id="lead-form" data-testid="lead-form-container">
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-testid="form-title">
            Request A Free Consultation
          </h2>
          <p className="text-gray-500">
            Get a personalized strategy for your business
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-form">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-700 flex items-center gap-2 font-medium">
              <User className="w-4 h-4 text-[#2979FF]" /> Full Name *
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="John Smith"
              value={formData.name}
              onChange={handleInputChange}
              className="form-input bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 h-12 rounded-xl focus:bg-white"
              data-testid="input-name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700 flex items-center gap-2 font-medium">
              <Mail className="w-4 h-4 text-[#2979FF]" /> Email Address *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="john@company.com"
              value={formData.email}
              onChange={handleInputChange}
              className="form-input bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 h-12 rounded-xl focus:bg-white"
              data-testid="input-email"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-gray-700 flex items-center gap-2 font-medium">
              <Phone className="w-4 h-4 text-[#2979FF]" /> Phone Number *
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData.phone}
              onChange={handleInputChange}
              className="form-input bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 h-12 rounded-xl focus:bg-white"
              data-testid="input-phone"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_name" className="text-gray-700 flex items-center gap-2 font-medium">
              <Building2 className="w-4 h-4 text-[#2979FF]" /> Company Name *
            </Label>
            <Input
              id="company_name"
              name="company_name"
              type="text"
              placeholder="Acme Inc."
              value={formData.company_name}
              onChange={handleInputChange}
              className="form-input bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 h-12 rounded-xl focus:bg-white"
              data-testid="input-company"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="website_url" className="text-gray-700 flex items-center gap-2 font-medium">
              <Globe className="w-4 h-4 text-[#2979FF]" /> Website URL
            </Label>
            <Input
              id="website_url"
              name="website_url"
              type="url"
              placeholder="https://example.com"
              value={formData.website_url}
              onChange={handleInputChange}
              className="form-input bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 h-12 rounded-xl focus:bg-white"
              data-testid="input-website"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full h-14 bg-[#2979FF] text-white hover:bg-[#1565C0] rounded-full text-lg font-semibold shadow-lg shadow-blue-500/30"
            data-testid="submit-button"
          >
            {isSubmitting ? "Submitting..." : "Request A Free Consultation"}
            {!isSubmitting && <ChevronRight className="w-5 h-5 ml-2" />}
          </Button>
        </form>

        <p className="text-center text-xs text-gray-400">
          Your information is secure and will never be shared
        </p>
      </div>
    </div>
  );
};

export default function LandingPage() {
  const testimonials = [
    {
      name: "Jill Rafilovich",
      company: "Client since 2014",
      quote: "We have utilized Brand Partners services for approx 10 years. Ed is very professional, responsive, prompt, personable and always takes the extra step. No matter how rapidly the Group has grown, no challenge is too small, and Ed makes every client feel important.",
      rating: 5
    },
    {
      name: "Vincent Servantez",
      company: "President, Cody Pools Florida",
      quote: "Brand Partners has been a key part of our historic growth for nearly a decade. Their team works as an extension of ours – aligned at every step, bolstered through constant communication, responsiveness & results.",
      rating: 5
    },
    {
      name: "David Adelstein",
      company: "Attorney at Law",
      quote: "This company will not disappoint you. They really care about delivering high-end services to the satisfaction of their clients at a very competitive price. You can tell that they really care about your business and your objectives.",
      rating: 5
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "We Analyze Your Market",
      description: "We find where your competitors are weak and where you can win fast on Google Maps.",
      color: "#00D4E5"
    },
    {
      step: "02",
      title: "We Build & Optimize",
      description: "On-page SEO + Google Business Profile dialed in perfectly.",
      color: "#2979FF"
    },
    {
      step: "03",
      title: "Rankings Start Moving",
      description: "Watch your business climb Google Maps while we track every call.",
      color: "#FF1744"
    },
    {
      step: "04",
      title: "You Get Results",
      description: "Dominate the local 3-pack and get more calls than ever before.",
      color: "#FF9100"
    }
  ];

  const faqs = [
    {
      question: "What makes Brand Partners different from other agencies?",
      answer: "With 20 years of experience since 2007, we've refined our approach to deliver consistent results in local SEO and Google Maps rankings. We focus on building long-term partnerships rather than short-term gains. Our award-winning team brings a unique blend of creativity and data-driven strategy to every project."
    },
    {
      question: "How long does it take to see results on Google Maps?",
      answer: "While every business is unique, most clients begin seeing measurable improvements in their Google Maps rankings within 2-4 weeks. Our proven strategies are designed to build sustainable growth, ensuring lasting results rather than temporary spikes."
    },
    {
      question: "What services do you offer?",
      answer: "We specialize in Local SEO, Google Business Profile optimization, Google Maps rankings, review management, local citation building, and comprehensive digital marketing strategies tailored to dominate your local market."
    },
    {
      question: "Do you work with businesses in my industry?",
      answer: "We've successfully worked with businesses across virtually every industry—from roofers and cleaners to dentists, lawyers, and restaurants. During our initial consultation, we'll assess how our expertise can best serve your specific market."
    },
    {
      question: "What does the consultation process look like?",
      answer: "Our free consultation is a no-pressure conversation about your business. We'll discuss your goals, analyze your current Google Maps presence, and provide actionable insights—whether you decide to work with us or not."
    },
    {
      question: "How do you measure success?",
      answer: "We believe in complete transparency. You'll receive regular reports showing your Google Maps rankings, call volume, website traffic, and ROI. We set clear KPIs at the start and track progress meticulously."
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Brand Partners" 
              className="h-10 md:h-12 object-contain"
              data-testid="logo-image"
            />
          </a>
          <div className="flex items-center gap-4">
            <a 
              href="tel:954-745-9545" 
              className="hidden sm:flex items-center gap-2 text-gray-700 font-semibold hover:text-[#2979FF] transition-colors"
              data-testid="nav-phone"
            >
              <Phone className="w-4 h-4" />
              954-745-9545
            </a>
            <Button 
              onClick={() => document.getElementById('lead-form').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#2979FF] text-white hover:bg-[#1565C0] rounded-full px-6 font-semibold shadow-lg shadow-blue-500/25"
              data-testid="nav-cta-button"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Focused on Map Rankings */}
      <section className="relative pt-28 md:pt-36 pb-16 overflow-hidden" data-testid="hero-section">
        <div className="hero-pattern-light" />
        
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Content */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="space-y-6"
            >
              {/* Trust Badges */}
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full text-sm font-medium text-gray-700">
                  <Star className="w-4 h-4 text-[#FF9100] fill-[#FF9100]" />
                  5 average rating
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-full text-sm font-medium text-gray-700">
                  <Clock className="w-4 h-4 text-[#2979FF]" />
                  Founded in 2007
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-full text-sm font-medium text-gray-700">
                  <Award className="w-4 h-4 text-[#7C3AED]" />
                  Multi-Award Winning Agency
                </span>
              </motion.div>

              {/* Main Headline */}
              <motion.h1 
                variants={fadeInUp}
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]"
                data-testid="hero-headline"
              >
                Dominate <span className="gradient-text-brand">Google Maps</span>
                <br />
                <span className="text-gray-900">In Your Local Area</span>
              </motion.h1>

              <motion.p 
                variants={fadeInUp}
                className="text-lg md:text-xl text-gray-600 max-w-xl leading-relaxed"
                data-testid="hero-subheadline"
              >
                20 years of experience. Award-winning agency. Nothing but 5-star reviews. Get more calls, more customers, and dominate your local market.
              </motion.p>

              {/* CTA Button */}
              <motion.div variants={fadeInUp} className="pt-4">
                <Button
                  onClick={() => document.getElementById('lead-form').scrollIntoView({ behavior: 'smooth' })}
                  className="btn-primary h-14 px-10 bg-[#2979FF] text-white hover:bg-[#1565C0] rounded-full text-lg font-semibold shadow-lg shadow-blue-500/30"
                  data-testid="hero-cta-button"
                >
                  Let us Prove It
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>

              {/* Sub CTA Text */}
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-6 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00C853]" /> Free 15-min call
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00C853]" /> No pitch, just strategy
                </span>
              </motion.div>
            </motion.div>

            {/* Right: Google Ranking Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <img 
                src={HERO_RANKING_IMAGE}
                alt="Google Maps Ranking Results"
                className="w-full h-auto rounded-2xl shadow-2xl"
                data-testid="hero-ranking-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial Highlights - Like Reference */}
      <section className="py-12 bg-gray-50" data-testid="testimonial-highlights">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#FF9100] text-[#FF9100]" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm mb-4 line-clamp-4">"{testimonial.quote}"</p>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">{testimonial.name}</div>
                  <div className="text-xs text-gray-500">{testimonial.company}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Form Section - MOVED TO MIDDLE */}
      <section className="py-20 md:py-28 section-blue-tint" data-testid="form-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            {/* Left: Form Info */}
            <motion.div variants={fadeInUp} className="space-y-6">
              <span className="inline-block text-sm uppercase tracking-widest text-[#2979FF] font-semibold bg-blue-50 px-4 py-2 rounded-full">Schedule Your FREE Strategy Call</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                15-minute call to learn our exact method
              </h2>
              <p className="text-gray-600 text-lg">
                (leave your wallet at home)
              </p>
              
              {/* Trust indicators */}
              <div className="flex items-center gap-4">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#FF9100] text-[#FF9100]" />
                  ))}
                </div>
                <span className="text-gray-600">5.0 rating</span>
              </div>
            </motion.div>

            {/* Right: Lead Form */}
            <motion.div variants={fadeInUp}>
              <LeadForm />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Join 1000+ Businesses Banner */}
      <section className="py-6 md:py-8 bg-[#2979FF]" data-testid="join-banner">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white leading-relaxed py-2">
              Join 1,000+ Businesses Getting Results Since 2007
            </h3>
          </div>
        </div>
      </section>

      {/* Before/After Results Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="results-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-12"
          >
            <motion.div variants={fadeInUp} className="text-center space-y-4">
              <span className="inline-block text-sm uppercase tracking-widest text-[#00C853] font-semibold bg-green-50 px-4 py-2 rounded-full">Real Results</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900" data-testid="results-title">
                Results of our clients
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                Drag the slider to see how we transform Google Maps rankings
              </p>
            </motion.div>

            {/* Two Comparison Sliders */}
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Miami Dental Group */}
              <motion.div variants={fadeInUp} className="space-y-4">
                <div className="bg-[#7C3AED] text-white p-4 rounded-t-xl text-center">
                  <p className="font-bold text-lg">Client: Miami Dental Group</p>
                  <p className="text-sm opacity-90">Ranked term: miami dentist</p>
                  <p className="text-sm opacity-90">Result: <span className="font-bold">64 New Patient Calls in 2 Months</span></p>
                </div>
                <ComparisonSlider 
                  beforeImage={MIAMI_BEFORE}
                  afterImage={MIAMI_AFTER}
                />
                <div className="result-card p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-500">6.33</div>
                        <div className="text-xs text-gray-500">Avg. Before</div>
                      </div>
                      <ChevronRight className="w-6 h-6 text-gray-300" />
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-500">Top 3</div>
                        <div className="text-xs text-gray-500">Avg. After</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                      <TrendingUp className="w-4 h-4" />
                      64 calls
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Buckeye Roofing */}
              <motion.div variants={fadeInUp} className="space-y-4">
                <div className="bg-[#7C3AED] text-white p-4 rounded-t-xl text-center">
                  <p className="font-bold text-lg">Client: Buckeye Roofing</p>
                  <p className="text-sm opacity-90">Ranked term: Cleveland roofer</p>
                  <p className="text-sm opacity-90">Result: <span className="font-bold">21 quotes in 2 weeks</span></p>
                </div>
                <ComparisonSlider 
                  beforeImage={BUCKEYE_BEFORE}
                  afterImage={BUCKEYE_AFTER}
                />
                <div className="result-card p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-500">5.62</div>
                        <div className="text-xs text-gray-500">Avg. Before</div>
                      </div>
                      <ChevronRight className="w-6 h-6 text-gray-300" />
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-500">Top 3</div>
                        <div className="text-xs text-gray-500">Avg. After</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                      <TrendingUp className="w-4 h-4" />
                      21 quotes
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* CTA */}
            <motion.div variants={fadeInUp} className="text-center pt-8">
              <Button
                onClick={() => document.getElementById('lead-form').scrollIntoView({ behavior: 'smooth' })}
                className="btn-primary h-14 px-10 bg-[#2979FF] text-white hover:bg-[#1565C0] rounded-full text-lg font-semibold shadow-lg shadow-blue-500/30"
              >
                Let us Prove It
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-16"
          >
            <motion.div variants={fadeInUp} className="text-center space-y-4">
              <span className="inline-block text-sm uppercase tracking-widest text-[#00D4E5] font-semibold bg-cyan-50 px-4 py-2 rounded-full">Our Process</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900" data-testid="process-title">
                How We Get You to the Top
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="card-hover relative p-8 bg-white border border-gray-100 rounded-2xl shadow-lg shadow-gray-100/50"
                  data-testid={`process-step-${index + 1}`}
                >
                  <div 
                    className="step-number mb-6"
                    style={{ backgroundColor: `${item.color}15`, color: item.color }}
                  >
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-16"
          >
            <motion.div variants={fadeInUp} className="text-center space-y-4">
              <span className="inline-block text-sm uppercase tracking-widest text-[#FF9100] font-semibold bg-orange-50 px-4 py-2 rounded-full">Why Brand Partners</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900" data-testid="why-title">
                Why Business Owners Choose Us
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "20 Years of Experience",
                  description: "Two decades of proven results in local SEO and Google Maps optimization since 2007.",
                  icon: Clock,
                  color: "#00D4E5"
                },
                {
                  title: "Award-Winning Team",
                  description: "Recognized by industry leaders for innovation and exceptional results.",
                  icon: Award,
                  color: "#FF9100"
                },
                {
                  title: "Nothing But 5-Star Reviews",
                  description: "Our clients love us—and the results speak for themselves.",
                  icon: Star,
                  color: "#2979FF"
                },
                {
                  title: "Local Market Domination",
                  description: "We help you own the Google Maps 3-pack in your service area.",
                  icon: MapPin,
                  color: "#FF1744"
                },
                {
                  title: "Dedicated Partnership",
                  description: "You get a team that's invested in your success as much as you are.",
                  icon: Users,
                  color: "#00D4E5"
                },
                {
                  title: "Transparent Reporting",
                  description: "Clear, honest communication and regular updates on your progress.",
                  icon: Target,
                  color: "#2979FF"
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="card-hover p-8 bg-white border border-gray-100 rounded-2xl shadow-lg shadow-gray-100/50"
                  data-testid={`why-card-${index + 1}`}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <item.icon className="w-7 h-7" style={{ color: item.color }} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* All Testimonials Section */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-16"
          >
            <motion.div variants={fadeInUp} className="text-center space-y-4">
              <span className="inline-block text-sm uppercase tracking-widest text-[#2979FF] font-semibold bg-blue-50 px-4 py-2 rounded-full">Testimonials</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900" data-testid="testimonials-title">
                What Our Clients Say
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="testimonial-card card-hover p-8 bg-white border border-gray-100 rounded-2xl shadow-lg shadow-gray-100/50"
                  data-testid={`testimonial-${index + 1}`}
                >
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#FF9100] text-[#FF9100]" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6">"{testimonial.quote}"</p>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.company}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Stats */}
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-12 pt-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2979FF]">5/5</div>
                <div className="text-gray-600">Rating</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#00D4E5]">1,000+</div>
                <div className="text-gray-600">Happy Clients</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#FF9100]">$1B+</div>
                <div className="text-gray-600">Revenue Generated</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-12"
          >
            <motion.div variants={fadeInUp} className="text-center space-y-4">
              <span className="inline-block text-sm uppercase tracking-widest text-[#FF1744] font-semibold bg-red-50 px-4 py-2 rounded-full">FAQ</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900" data-testid="faq-title">
                Frequently Asked Questions
              </h2>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
                {faqs.map((faq, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`}
                    className="faq-item bg-gray-50 border border-gray-100 rounded-xl px-6 overflow-hidden shadow-sm"
                    data-testid={`faq-item-${index + 1}`}
                  >
                    <AccordionTrigger className="text-left text-lg font-medium hover:no-underline py-6 text-gray-900">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pb-6">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-28 relative overflow-hidden bg-gradient-to-br from-[#2979FF] to-[#00D4E5]" data-testid="cta-section">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-cyan-200 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 relative">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center space-y-8"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white"
              data-testid="cta-title"
            >
              Ready to Dominate Google Maps?
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-white/90 max-w-2xl mx-auto"
            >
              Join 1,000+ businesses that have transformed their local presence since 2007. Let's discuss how we can help you get more calls and customers.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => document.getElementById('lead-form').scrollIntoView({ behavior: 'smooth' })}
                className="btn-primary h-14 px-10 bg-white text-[#2979FF] hover:bg-gray-100 rounded-full text-lg font-semibold shadow-xl"
                data-testid="cta-button"
              >
                Request A Free Consultation
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-6 pt-4 text-sm text-white/80"
            >
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" /> Free Strategy Session
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" /> No Obligation
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" /> Expert Insights
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-gray-100" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img 
              src={LOGO_URL} 
              alt="Brand Partners" 
              className="h-10 object-contain"
            />
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Brand Partners. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
